import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { supabase } from './supabase';
import { Permission } from '../types';

export type Role = 'admin' | 'director' | 'teacher';

export interface User {
  id: string;
  username: string;
  email: string;
  role: Role;
  teacherId?: string;
  centerId: string;
  name?: string;
  created_at?: string;
  permissions?: Permission[];
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  restoreSession: () => Promise<void>;
  changeOwnPassword: (newPassword: string) => Promise<void>;
  hasPermission: (permission: Permission) => boolean;
  switchCenter: (centerId: string) => void;
}

const PERFIL = 'id, username, email, role, teacher_id, center_id, is_active, permissions';

/** Traduce el usuario que introduce la persona (nombre de usuario o email) a email. */
async function resolverEmail(identificador: string): Promise<string> {
  if (identificador.includes('@')) return identificador.trim();

  const { data, error } = await supabase.rpc('email_for_username', { p_username: identificador.trim() });
  if (error) throw new Error('No se ha podido verificar el usuario. Revisa tu conexión.');
  if (!data) throw new Error('Credenciales inválidas');
  return data as string;
}

/** Carga la fila de public.users asociada a la sesión de Supabase Auth. */
async function cargarPerfil(authId: string): Promise<User> {
  const { data, error } = await supabase
    .from('users').select(PERFIL).eq('auth_id', authId).maybeSingle();

  if (error) throw new Error('No se ha podido cargar el perfil del usuario.');
  if (!data) {
    // Existe la cuenta en Auth pero no está enlazada con la tabla de la app.
    await supabase.auth.signOut();
    throw new Error('Tu cuenta no está vinculada a ningún usuario de la aplicación. Avisa al administrador.');
  }
  if (!data.is_active) {
    await supabase.auth.signOut();
    throw new Error('Tu cuenta está desactivada.');
  }

  // Para los profesores se usa su nombre de la ficha; el resto de cuentas
  // (admin, dirección) no tienen ficha y se quedan con el nombre de usuario.
  let nombre = data.username as string;
  if (data.teacher_id) {
    const { data: ficha } = await supabase
      .from('teachers').select('first_name, last_name').eq('id', data.teacher_id).maybeSingle();
    if (ficha?.first_name) nombre = `${ficha.first_name} ${ficha.last_name ?? ''}`.trim();
  }

  return {
    id: data.id as string,
    username: data.username as string,
    email: data.email as string,
    role: data.role as Role,
    teacherId: (data.teacher_id as string) ?? undefined,
    centerId: data.center_id as string,
    name: nombre,
    permissions: (data.permissions as Permission[]) || [],
  };
}

export const useAuth = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,

      login: async (username: string, password: string) => {
        const email = await resolverEmail(username);

        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error || !data.user) throw new Error('Credenciales inválidas');

        const user = await cargarPerfil(data.user.id);
        set({ user, isAuthenticated: true });
      },

      logout: async () => {
        await supabase.auth.signOut();
        set({ user: null, isAuthenticated: false });
      },

      /**
       * Revalida contra Supabase al arrancar: el perfil persistido en
       * localStorage no basta como prueba de sesión, la sesión real la
       * gestiona supabase-js y puede haber caducado.
       */
      restoreSession: async () => {
        const { data } = await supabase.auth.getSession();
        if (!data.session) {
          set({ user: null, isAuthenticated: false });
          return;
        }
        try {
          const user = await cargarPerfil(data.session.user.id);
          set({ user, isAuthenticated: true });
        } catch {
          set({ user: null, isAuthenticated: false });
        }
      },

      changeOwnPassword: async (newPassword: string) => {
        const { error } = await supabase.auth.updateUser({ password: newPassword });
        if (error) throw new Error(error.message);
      },

      hasPermission: (permission: Permission) => {
        const { user } = get();
        if (!user) return false;
        if (user.role === 'admin' || user.role === 'director') return true;
        return user.permissions?.includes(permission) || false;
      },

      switchCenter: (centerId: string) => {
        const { user } = get();
        if (user && user.role === 'admin') {
          set({ user: { ...user, centerId } });
        }
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
    }
  )
);

// ── Recuperación de contraseña por código de un solo uso ──
//
// Se usa código y no enlace porque el correo abre el navegador del sistema,
// que no puede devolver la sesión a la aplicación de escritorio. Con código,
// todo el proceso ocurre dentro de la app y funciona igual en web y Electron,
// sin depender de URLs de redirección.
//
// Requiere que la plantilla "Reset Password" de Supabase incluya {{ .Token }}.
// La longitud del código es configurable en Supabase (este proyecto usa 8),
// así que ni el formulario ni estas funciones deben asumir un tamaño fijo.

/** Envía al correo del usuario un código de recuperación de un solo uso. */
export async function solicitarCodigoRecuperacion(identificador: string): Promise<string> {
  const email = await resolverEmail(identificador);
  const { error } = await supabase.auth.resetPasswordForEmail(email);
  // No se distingue "no existe" de "enviado": revelarlo permitiría averiguar
  // qué cuentas existen. El mensaje al usuario es el mismo en ambos casos.
  if (error && !/user not found/i.test(error.message)) throw new Error(error.message);
  return email;
}

/** Valida el código recibido y establece la nueva contraseña. */
export async function confirmarRecuperacion(email: string, codigo: string, nuevaPassword: string): Promise<void> {
  const { error: errorOtp } = await supabase.auth.verifyOtp({
    email,
    token: codigo.trim(),
    type: 'recovery',
  });
  if (errorOtp) {
    // Se conserva el motivo de Supabase: un mensaje fijo ocultaba que el
    // problema real era un código truncado por el formulario.
    const detalle = /expired/i.test(errorOtp.message) ? 'ha caducado'
      : /invalid/i.test(errorOtp.message) ? 'no es correcto o ya se ha usado'
      : errorOtp.message;
    throw new Error(`El código ${detalle}. Pide uno nuevo y cópialo completo.`);
  }

  const { error } = await supabase.auth.updateUser({ password: nuevaPassword });
  if (error) throw new Error(error.message);

  // La verificación del código deja sesión abierta; se cierra para obligar a
  // entrar con la contraseña nueva y confirmar que ha quedado bien guardada.
  await supabase.auth.signOut();
}

/** Nombre del rol tal como debe leerlo una persona, no como se guarda. */
export const ROLE_LABELS: Record<Role, string> = {
  admin: 'Administración',
  director: 'Dirección',
  teacher: 'Profesorado',
};

export const useIsAdmin = () => {
  const user = useAuth((state) => state.user);
  return user?.role === 'admin' || user?.role === 'director';
};

export const useIsGlobalAdmin = () => {
  const user = useAuth((state) => state.user);
  return user?.role === 'admin';
};

export const useIsTeacher = () => {
  const user = useAuth((state) => state.user);
  return user?.role === 'teacher';
};

export const useHasPermission = (permission: Permission) => {
  return useAuth((state) => state.hasPermission(permission));
};
